import { getRoomPrice } from '@koob/utils';
import { generateRoomKey } from './generateRoomKey';

// KD-3594 D4 — run every time a hotel day's rooms-availability query gets a
// fresh `requestId` (on expiry, see useHotelRoomAvailability's
// `refetchInterval`) for a day that already has a room selected. Matches each
// previously selected room against the new possibilities by name + the bed it
// was booked on — the same identity a room keeps across two availability
// calls, since a supplier's own ordering of `possibilities` is not stable.
interface ReconciledRoom {
  name: string;
  bedChoices?: unknown[];
  bed?: { id?: string } | null;
  [key: string]: unknown;
}

interface ReconcileRoomSelectionResult {
  status: 'same' | 'priceChanged' | 'unavailable';
  rooms: ReconciledRoom[];
  roomKeys: string;
  totalPrice: number;
}

function selectedRoomsOf(selection: any): any[] {
  if (selection?.allRooms?.length) return selection.allRooms;
  return selection?.roomData ? [selection.roomData] : [];
}

function matchRoom(selectedRoom: any, possibilities: any[]) {
  const bedId = selectedRoom?.bed?.id ?? selectedRoom?.bedChoices?.[0]?.id;
  const room = possibilities.find(
    (p) =>
      p.name === selectedRoom?.name &&
      p.bedChoices?.some((b: any) => b.id === bedId),
  );
  if (!room) return null;

  return {
    ...room,
    bed: room.bedChoices.find((b: any) => b.id === bedId) ?? room.bedChoices[0],
  };
}

// `previousTotalPrice` comes from `day.hotelPricing.price`, not from a field
// on `selection` — `updateHotelRoomSelection` (tripSlice.ts) never stores a
// `totalPrice` on `day.selectedRoom` itself, only on the day's separate
// `hotelPricing`, which is the one place a room's confirmed total lives.
export function reconcileRoomSelection(
  selection: any,
  possibilities: any[],
  previousTotalPrice: number | null | undefined,
): ReconcileRoomSelectionResult {
  const selectedRooms = selectedRoomsOf(selection);
  const matchedRooms = selectedRooms.map((room) =>
    matchRoom(room, possibilities ?? []),
  );

  if (matchedRooms.some((room) => !room)) {
    return {
      status: 'unavailable',
      rooms: selectedRooms.map((room) => ({ name: room.name })),
      roomKeys: selection?.roomKey ?? '',
      totalPrice: NaN,
    };
  }

  const rooms = matchedRooms as ReconciledRoom[];
  const totalPrice = rooms.reduce((sum, room) => sum + getRoomPrice(room), 0);
  const roomKeys = rooms
    .map((room, index) => generateRoomKey(room, index, room.bed?.id ?? null))
    .join(',');

  const status =
    previousTotalPrice != null && previousTotalPrice !== totalPrice
      ? 'priceChanged'
      : 'same';

  return { status, rooms, roomKeys, totalPrice };
}
